import { useCallback } from 'react';
import { useTranslations } from 'js/hooks/i18n';
import { prop, pick } from 'js/utils/ramda.util';
import { getScript } from 'js/utils/scripts.util';
import { useGetURLTemplates } from 'js/hooks/useGetURLTemplates';
import { useGetTemporaryAuthenticationToken } from 'js/hooks/auth';
import { useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { useComponentConfig, useComponentMethods } from 'js/hooks/component';

import * as constants from '../constants';
import { useGetSportsbookConfig } from './useGetSportsbookConfig';
import { useGetNavigationParams } from './useGetNavigationParams';

export const useInitSportsbook = () => {
    const translate = useTranslations();
    const getURLTemplates = useGetURLTemplates();
    const sportsConfig = useGetSportsbookConfig();
    const navigationParams = useGetNavigationParams();
    const { showComponentError } = useComponentMethods();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const { sportsbookDesktopURL, sportsbookMobileURL } = useComponentConfig();
    const getTemporaryAuthenticationToken = useGetTemporaryAuthenticationToken();
    const { isMobile, isTablet } = useDeviceSelector(pick(['isMobile', 'isTablet']));

    return useCallback(async () => {
        let token;
        let sportsbookAPIURL = isMobile || isTablet ? sportsbookMobileURL : sportsbookDesktopURL;

        if (!sportsbookAPIURL) {
            try {
                const URLTemplates = await getURLTemplates({ urlTypeList: [constants.SPORTSBOOK_URL_TEMPLATE_TYPE] });

                sportsbookAPIURL = URLTemplates?.data?.urlList?.[0]?.url;
            } catch {
                showComponentError(translate('playtech.mobile.sportsbook-betconstruct.sportsbookAPIURL.error'));

                return;
            }
        }

        if (!sportsbookAPIURL) {
            showComponentError(translate('playtech.mobile.sportsbook-betconstruct.sportsbookAPIURL.error'));

            return;
        }

        if (isLoggedIn) {
            token = await getTemporaryAuthenticationToken();
        }

        // the only way to force betconstruct to refresh itself;
        document.getElementById(constants.SPORTSBOOK_HTML_CONTAINER_ID)?.replaceChildren();

        const url = new URL(sportsbookAPIURL);
        const newSearchParams = new URLSearchParams({
            ...Object.fromEntries(url.searchParams),
            ...sportsConfig,
            ...navigationParams,
            ...token ? { AuthToken: token } : {}
        });

        url.search = newSearchParams.toString();

        await getScript(url.toString(), undefined, constants.SPORTSBOOK_SCRIPT_ID);
        // to avoid unnecessary updates we don't need all deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translate, navigationParams, isMobile]);
};
